import { Forecast } from '../../../../Services/API/forecastApi';
import { RiskLevel, RiskProfile } from '../../../../Services/API/riskProfileApi';
import moment from 'moment';
import { getCurrentForecastValue } from '../WorkRestHelpers';
import { WorkRestProfile } from '../../../../Services/API/workRestApi';

type HighForecastHourRange = { startTime?: string; endTime?: string; startValue?: number };

export const getRiskLevelMeta = (
  riskLevels: RiskProfile['riskLevels'] | undefined,
  value: number
): {
  name?: string;
  color?: string;
  colorSecondary?: string
  id?: number;
  description?: string;
  sortedLevels: RiskLevel[];
} => {
  if (!riskLevels || riskLevels.length === 0) return { sortedLevels: [] };

  const sorted = [...riskLevels].sort((a, b) => a.max - b.max);
  const level = sorted.find(l => value <= l.max) || sorted[sorted.length - 1];

  return {
    name: level.name,
    color: level.color,
    colorSecondary: level.colorSecondary,
    id: level.id,
    description: level.description,
    sortedLevels: sorted,
  };
};

export const getColorForTempValue = (value: number, levels: RiskLevel[]) => {
  const sortedLevels = [...levels].sort((a, b) => a.max - b.max);

  for (let i = 0; i < sortedLevels.length; i++) {
    const current = sortedLevels[i];
    const previousMax = i === 0 ? Number.NEGATIVE_INFINITY : sortedLevels[i - 1].max;

    // Match the value to its corresponding range
    if (value > previousMax && value <= current.max) {
      return current.color;
    }
  }

  // Fallback to the last color if no exact match is found
  return sortedLevels[sortedLevels.length - 1]?.color || '#000';
};

/**
 * Generates gradient stops based on risk levels and an adjusted value range.
 * Adds smooth transitions (fade effect) between different risk colors.
 */
export const getGradientStops = (levels: RiskLevel[], adjustedRange: [number, number]) => {
  const [actualMin, actualMax] = adjustedRange;
  const range = actualMax - actualMin;
  const fadePercent = 7; // Controls the softness of color transitions

  if (range <= 0 || levels.length === 0) {
    console.log('range', range);
    // No valid range or levels, fallback to last color
    const fallbackColor = levels[levels.length - 1]?.color ?? '#000';
    return [
      { offset: '0%', color: fallbackColor },
      { offset: '100%', color: fallbackColor },
    ];
  }

  const sorted = [...levels].sort((a, b) => a.max - b.max);
  const stops: { offset: string; color: string }[] = [];

  // Build gradient stops with a fade effect between adjacent risk levels
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const prevMax = i === 0 ? actualMin : sorted[i - 1].max;
    const start = ((prevMax - actualMin) / range) * 100;
    const end = ((current.max - actualMin) / range) * 100;

    const fadeInStart = Math.max(0, start - fadePercent);
    const fadeInEnd = Math.min(100, start + fadePercent);
    const fadeOutStart = Math.max(0, end - fadePercent);
    const fadeOutEnd = Math.min(100, end + fadePercent);

    if (i === 0) {
      // Initial color stop at the start of the gradient
      stops.push({ offset: `0%`, color: current.color });
    } else {
      // Smooth transition from previous color to current
      stops.push({ offset: `${fadeInStart}%`, color: stops[stops.length - 1].color });
      stops.push({ offset: `${fadeInEnd}%`, color: current.color });
    }

    // Flat color area between fades
    stops.push({ offset: `${fadeOutStart}%`, color: current.color });

    if (i === sorted.length - 1) {
      // Final stop at the end of the gradient
      stops.push({ offset: `100%`, color: current.color });
    } else {
      // Start fading towards the next color
      const nextColor = sorted[i + 1].color;
      stops.push({ offset: `${fadeOutEnd}%`, color: nextColor });
    }
  }

  return stops;
};

export const getRiskLevel = (riskProfile: RiskProfile | undefined, observationValue: number | undefined) => {
  if (!riskProfile || !observationValue || riskProfile.riskLevels?.length === 0) {
    return undefined;
  }

  const riskLevel =
    riskProfile.riskLevels.find(level => observationValue <= level.max) ??
    riskProfile.riskLevels[riskProfile.riskLevels.length - 1];

  return riskLevel;
};

export const getHighForecastHourRange = (
  hourlyForecast: Forecast[] | undefined,
  riskProfile: RiskProfile | undefined,
  workRestProfile: WorkRestProfile | undefined,
  modifier: number
): HighForecastHourRange | undefined => {
  if (!hourlyForecast || hourlyForecast.length === 0) {
    return undefined;
  }

  const highestValue = Math.max(...hourlyForecast.map(f => getCurrentForecastValue(f, workRestProfile) ?? 0));
  const peakIndex = hourlyForecast.find(
    forecast => getCurrentForecastValue(forecast, workRestProfile) === highestValue
  );

  if (!peakIndex) {
    return undefined;
  }

  if (!riskProfile) {
    return {
      startTime: peakIndex.observationTime?.toString(),
      startValue: highestValue,
    };
  }

  const riskLevel = getRiskLevel(riskProfile, highestValue + modifier);

  if (!riskLevel) {
    return undefined;
  }

  let start: Forecast | undefined;
  for (const forecast of hourlyForecast) {
    const forecastValue = getCurrentForecastValue(forecast, workRestProfile);
    if (forecastValue == null) {
      continue;
    }
    const currentRiskLevel = getRiskLevel(riskProfile, forecastValue + modifier);

    if (currentRiskLevel?.id === riskLevel.id) {
      start = forecast;
      break;
    }
  }
  const reversed = [...hourlyForecast].reverse();
  let end: Forecast | undefined;
  for (const forecast of reversed) {
    const forecastValue = getCurrentForecastValue(forecast, workRestProfile);
    if (forecastValue == null) {
      continue;
    }
    const currentRiskLevel = getRiskLevel(riskProfile, forecastValue + modifier);
    if (currentRiskLevel?.id === riskLevel.id) {
      end = forecast;
      break;
    }
  }

  return {
    startTime: start?.observationTime?.toString(),
    endTime: end?.observationTime?.toString(),
  };
};

export const getHighForecastRangeString = (
  metric: string | undefined,
  highForecastHourRange: HighForecastHourRange | undefined
) => {
  if (!highForecastHourRange) {
    return '';
  }

  const startMoment = moment(highForecastHourRange.startTime);
  const endMoment = moment(highForecastHourRange.endTime);

  const startFormat = startMoment.format('h');
  const startPeriod = startMoment.format('A');
  const endFormat = endMoment.format('h');
  const endPeriod = endMoment.format('A');
  const validation = startFormat.concat(startPeriod) === endFormat.concat(endPeriod);

  if (highForecastHourRange.startTime && highForecastHourRange.startValue) {
    return `The high ${metric} of ${highForecastHourRange.startValue.toFixed(1)}°F is expected at ${moment(
      highForecastHourRange.startTime
    ).format('hA')}.`;
  }

  if (validation) {
    return `Highest ${metric} around ${startFormat}${startPeriod}.`;
  } else {
    return `Highest ${metric} from ${startFormat}${startPeriod} to ${endFormat}${endPeriod}.`;
  }
};
